import { PERISCOPE_TAB_TYPES } from '@sonar-software/sonar-ts-constants';
import { SelectionTable } from '@ui/tables';
import { ValidationRuleRow } from './ValidationRuleRow';
import { ValidationRuleSearch } from './ValidationRuleSearch';
import {
	getRowKey,
	paginationDefaultQueryParams,
	paginationFallbackQueryParams,
	PaginationQueryParams,
	PeriscopePagination
} from '../';
import { InactiveToggleQueryParams, SearchFilterQueryParams } from '../filters';
import { OffsetPaginatedResource, PeriscopeSfValidationRule, SonarObjectType } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useRef } from 'react';
import { useQueryResource, useSelectionTable } from '@hooks';

type Resource = OffsetPaginatedResource<PeriscopeSfValidationRule>;
export type ValidationRuleSearchableSelectionTableQueryParams = InactiveToggleQueryParams &
	PaginationQueryParams &
	SearchFilterQueryParams;

export interface ValidationRuleSearchableSelectionTableProps {
	parentIndex: number;
	resourceIdsUri?: string;
	uri: string;
}

export const ValidationRuleSearchableSelectionTable: React.FC<ValidationRuleSearchableSelectionTableProps> = ({
	parentIndex,
	resourceIdsUri,
	uri
}) => {
	const scrollableListRef = useRef<HTMLDivElement>(null);

	const { loading, resource, queryParams, updateParams } = useQueryResource<
		Resource,
		ValidationRuleSearchableSelectionTableQueryParams
	>({
		defaultParams: {
			...paginationDefaultQueryParams,
			hideInactive: true
		},
		fallbackParams: paginationFallbackQueryParams,
		uri
	});

	const selectionData = useSelectionTable({
		getRowKey,
		queryParams,
		resourceIdsUri: resourceIdsUri,
		results: resource?.results,
		selectionType: SonarObjectType.SalesforceValidationRule
	});

	const renderValidationRule = useCallback(
		(rule: PeriscopeSfValidationRule) =>
			<ValidationRuleRow
				key={ rule.sonarObjectId }
				parentIndex={ parentIndex }
				rule={ rule } />
		,
		[ parentIndex ]
	);

	const results = resource?.results;

	return (
		<>
			<ValidationRuleSearch
				onChange={ updateParams }
				queryParams={ queryParams } />
			<SelectionTable
				dataSource={ results }
				forceShowSelection={ resourceIdsUri ? undefined : false }
				loading={ loading }
				ref={ scrollableListRef }
				renderRow={ renderValidationRule }
				rowKey={ getRowKey }
				selectionData={ selectionData }
				selectionType={ PERISCOPE_TAB_TYPES.VALIDATION_RULES } />
			<PeriscopePagination
				loading={ loading }
				onChange={ updateParams }
				queryParams={ queryParams }
				scrollRef={ scrollableListRef }
				totalCount={ Number(resource?.totalCount) } />
		</>
	);
};
